/**
 * Configurable interaction / pattern rules. Tune thresholds and per-rule point impacts here.
 * Rules only fire when every required input is present and passes the predicate (all-or-nothing).
 */

module.exports = {
  /** Max total points added to base score from all matched interactions (before final min(100,...)). */
  maxTotalInteractionPoints: 32,

  /**
   * Each rule: impactPoints (integer), requiredFactors (documentation), patternPhrase (narrative fragment),
   * and thresholds used by the evaluator.
   */
  rules: [
    {
      interactionKey: 'early_tenure_below_market_external_pull',
      impactPoints: 6,
      requiredFactors: [
        'hireDate / tenureYears',
        'salary + marketSalary',
        'externalRoleDemandIndex',
      ],
      patternPhrase:
        'below-market pay in a high-demand role during early tenure',
      thresholds: {
        maxTenureYears: 1,
        minMarketGapPct: 0.5,
        minExternalDemand: 55,
      },
    },
    {
      interactionKey: 'promotion_stagnation_strong_perf_low_recognition',
      impactPoints: 7,
      requiredFactors: [
        'lastPromotionDate',
        'latestPerformanceRating OR performanceRatings',
        'recognitionEventsPerYear',
      ],
      patternPhrase:
        'long time without promotion despite strong performance and sparse recognition',
      thresholds: {
        minYearsSincePromotion: 2.5,
        minPerformanceRating: 4,
        maxRecognitionPerYear: 3,
      },
    },
    {
      interactionKey: 'manager_churn_team_attrition_worsening',
      impactPoints: 6,
      requiredFactors: ['managerChangeCount12Mo', 'teamAttritionTrend OR team attrition rates'],
      patternPhrase: 'manager churn alongside a worsening team attrition trend',
      thresholds: {
        minManagerChanges: 2,
        minTeamAttritionTrend: 3,
      },
    },
    {
      interactionKey: 'overload_productivity_absenteeism',
      impactPoints: 8,
      requiredFactors: [
        'workloadPressureScore',
        'productivityChangeIndex OR productivity trend',
        'absenteeismChangePct OR absenteeism trend',
      ],
      patternPhrase: 'rising workload with slipping productivity and increasing absenteeism',
      thresholds: {
        minWorkload: 60,
        minProductivityStress: 50,
        minAbsenteeismStress: 5,
      },
    },
    {
      interactionKey: 'high_performer_compensation_lag',
      impactPoints: 7,
      requiredFactors: [
        'latestPerformanceRating OR performanceRatings',
        'salary + marketSalary OR peer median',
      ],
      patternPhrase: 'strong performance paired with compensation lag vs market or peers',
      thresholds: {
        minPerformanceRating: 4,
        minMarketGapPct: 5,
        maxPeerSalaryRatio: 0.95,
      },
    },
    {
      interactionKey: 'leadership_reorg_budget_pressure',
      impactPoints: 5,
      requiredFactors: [
        'leadershipChangeExposureIndex',
        'reorgCount12Mo',
        'budgetPressureSignal',
      ],
      patternPhrase: 'leadership change exposure, reorgs, and budget pressure together',
      thresholds: {
        minLeadershipExposure: 50,
        minReorgs: 2,
        minBudgetPressure: 50,
      },
    },
    {
      interactionKey: 'low_mobility_unclear_promotion_path',
      impactPoints: 5,
      requiredFactors: ['internalMobilityIndex', 'promotionPathClarityScore'],
      patternPhrase: 'limited internal mobility options with an unclear promotion path',
      thresholds: {
        maxInternalMobility: 40,
        maxPromotionClarity: 45,
      },
    },
    {
      interactionKey: 'collaboration_and_checkin_withdrawal',
      impactPoints: 5,
      requiredFactors: [
        'collaborationDropIndex OR collaboration trend',
        'managerCheckinDropIndex OR check-in trend',
      ],
      patternPhrase: 'dropping collaboration signals and fewer manager check-ins',
      thresholds: {
        minDropIndex: 45,
      },
    },
    {
      interactionKey: 'comp_stress_cluster_attrition_worsening',
      impactPoints: 6,
      requiredFactors: [
        'department peer comp (cluster density)',
        'teamAttritionTrend OR team attrition rates',
      ],
      patternPhrase:
        'widespread below-market pay on the team together with worsening attrition',
      thresholds: {
        minClusterBelowMarketShare: 0.45,
        minTeamAttritionTrend: 3,
      },
    },
    {
      interactionKey: 'critical_role_external_pull_underpay',
      impactPoints: 8,
      requiredFactors: [
        'roleCriticality',
        'externalRoleDemandIndex',
        'salary + marketSalary',
      ],
      patternPhrase:
        'a critical role facing strong external demand while pay trails the market',
      thresholds: {
        minRoleCriticality: 4,
        minExternalDemand: 50,
        minMarketGapPct: 8,
      },
    },
    {
      interactionKey: 'critical_role_burnout',
      impactPoints: 7,
      requiredFactors: [
        'roleCriticality',
        'workloadPressureScore',
        'absenteeismChangePct OR absenteeism trend',
      ],
      patternPhrase:
        'a critical role under sustained workload pressure with rising absenteeism',
      thresholds: {
        minRoleCriticality: 4,
        minWorkload: 65,
        minAbsenteeismStress: 5,
      },
    },
    {
      interactionKey: 'tenured_stagnation_no_mobility',
      impactPoints: 6,
      requiredFactors: [
        'hireDate / tenureYears',
        'lastPromotionDate',
        'internalMobilityIndex',
        'recognitionEventsPerYear',
      ],
      patternPhrase:
        'long tenure without promotion, limited internal mobility, and sparse recognition',
      thresholds: {
        minTenureYears: 5,
        minYearsSincePromotion: 3,
        maxInternalMobility: 45,
        maxRecognitionPerYear: 3,
      },
    },
    {
      interactionKey: 'reorg_mobility_freeze_manager_change',
      impactPoints: 6,
      requiredFactors: [
        'reorgCount12Mo',
        'managerChangeCount12Mo',
        'internalMobilityIndex',
      ],
      patternPhrase:
        'recent reorgs and manager change combined with frozen internal mobility',
      thresholds: {
        minReorgs: 1,
        minManagerChanges: 1,
        maxInternalMobility: 40,
      },
    },
    {
      interactionKey: 'reward_mismatch_high_performer_market_lag',
      impactPoints: 7,
      requiredFactors: [
        'performanceRewardMismatchIndex',
        'latestPerformanceRating',
        'salary + marketSalary',
      ],
      patternPhrase:
        'strong performance with an internal reward mismatch and below-market pay',
      thresholds: {
        minRewardMismatch: 55,
        minMarketGapPct: 5,
      },
    },
  ],
};
